// src/pages/TransactionsPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./TransactionsPage.css";
import { API_BASE } from "../config";
import categories from "../data/categories";

/* ---------- Helpers ---------- */

const eurFormatter = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
});

const dateFormatter = new Intl.DateTimeFormat("it-IT", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return eurFormatter.format(n);
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return dateFormatter.format(d);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function normalizeText(s) {
  return String(s || "").toLowerCase().trim();
}

function joinUrl(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  if (!b) return `/${p}`; // stessa origin
  return `${b}/${p}`;
}

/**
 * ✅ Upload URL (protetto)
 * Backend serve: GET /api/uploads/:file
 * receiptPath può essere:
 *  - "tenant__...pdf"
 *  - "/api/uploads/tenant__...pdf"
 *  - "/uploads/tenant__...pdf" (legacy)
 */
function buildUploadUrl(path) {
  if (!path) return "";
  const asString = String(path);

  // già assoluto
  if (/^https?:\/\//i.test(asString)) return asString;

  const clean = asString.replace(/^\/+/, ""); // rimuove slash iniziali

  // già in formato api/uploads/...
  if (/^api\/uploads\//i.test(clean)) return joinUrl(API_BASE, clean);

  // legacy uploads/...
  if (/^uploads\//i.test(clean)) {
    return joinUrl(API_BASE, clean.replace(/^uploads\//i, "api/uploads/"));
  }

  // solo filename
  return joinUrl(API_BASE, `api/uploads/${clean}`);
}

async function readErrorMessage(res) {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const data = await res.json().catch(() => null);
    if (!data) return `Errore HTTP ${res.status}`;
    return data.message || data.error || `Errore HTTP ${res.status}`;
  }
  const text = await res.text().catch(() => "");
  return text || `Errore HTTP ${res.status}`;
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

/** ✅ Ruoli robusti (supporta user.roles array + fallback user.role singolo) */
function normalizeRoles(user) {
  const roles = Array.isArray(user?.roles) ? user.roles.slice() : [];
  if (roles.length === 0 && user?.role) roles.push(user.role);
  return roles.map((r) => String(r).trim().toLowerCase()).filter(Boolean);
}

function hasAnyRole(user, allowed) {
  const roles = normalizeRoles(user);
  return allowed.some((r) => roles.includes(String(r).toLowerCase()));
}

/** ✅ Colore categoria robusto */
function getCategoryColor(categoryName) {
  const found = categories.find((c) => c.name === categoryName);
  return found?.color || "var(--tx-muted)";
}

/** ✅ Categoria -> oggetto (name/type/color) */
function getCategoryObj(categoryName) {
  return categories.find((c) => c.name === categoryName) || null;
}

/** ✅ Tipo derivato da importo */
function typeFromAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  return n < 0 ? "Uscita" : "Entrata";
}

/* ---------- UI components ---------- */

function Badge({ variant = "muted", children }) {
  return <span className={`tx-badge tx-badge--${variant}`}>{children}</span>;
}

function Dot({ color }) {
  return (
    <span
      className="tx-dot"
      style={{ backgroundColor: color || "var(--tx-muted)" }}
      aria-hidden="true"
    />
  );
}

/* ---------- Main Page ---------- */

export default function TransactionsPage({ token, user }) {
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | approved | pending
  const [selectedId, setSelectedId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [error, setError] = useState("");

  // ✅ MODAL EDIT
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editTx, setEditTx] = useState(null);

  // ✅ MODAL FILTRI AVANZATI
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    supplierId: "",
    dateFrom: "",
    dateTo: "",
    year: "",
    category: "",
    type: "", // "Entrata" | "Uscita" | ""
    approval: "", // "approved" | "pending" | ""
    description: "",
  });

  // Suppliers (usati sia per edit che filtri)
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  // ✅ query separate per UX (separiamo filtri da edit, così non si pestano)
  const [supplierQueryFilter, setSupplierQueryFilter] = useState("");
  const [supplierQueryEdit, setSupplierQueryEdit] = useState("");

  // focus UX: quando selezioni fornitore → focus su metodo pagamento (nel modal)
  const payMethodRef = useRef(null);

  // abort per fetch transazioni
  const abortTxRef = useRef(null);

  /** ✅ Permessi (multi-ruolo) */
  const canApprove = useMemo(
    () => hasAnyRole(user, ["admin", "segretario", "tesoriere", "developer"]),
    [user]
  );
  const canDelete = useMemo(
    () => hasAnyRole(user, ["admin", "segretario", "tesoriere", "developer"]),
    [user]
  );
  const canEdit = useMemo(
    () => hasAnyRole(user, ["admin", "segretario", "tesoriere", "developer"]),
    [user]
  );

  /** ✅ Modifica anche se APPROVATA */
  const canEditApproved = useMemo(
    () => hasAnyRole(user, ["admin", "segretario", "tesoriere", "developer"]),
    [user]
  );

  const paymentIcons = useMemo(
    () => ({
      Bonifico: "🏦",
      Contanti: "💶",
      "Pagamento Elettronico": "💳",
    }),
    []
  );

  const selectedTx = useMemo(
    () => transactions.find((t) => t.id === selectedId) || null,
    [transactions, selectedId]
  );

  /* =========================================================
     FILTRI AVANZATI (client-side)
     ========================================================= */

  const years = useMemo(() => {
    const set = new Set();
    for (const t of transactions) {
      const d = new Date(t?.data);
      if (!Number.isNaN(d.getTime())) set.add(String(d.getFullYear()));
    }
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [transactions]);

  const categoryOptions = useMemo(() => categories.map((c) => c.name), []);

  const visibleTransactions = useMemo(() => {
    const f = filters;

    const fromMs = f.dateFrom ? new Date(f.dateFrom).setHours(0, 0, 0, 0) : null;
    const toMs = f.dateTo ? new Date(f.dateTo).setHours(23, 59, 59, 999) : null;

    const descQ = normalizeText(f.description);

    return transactions.filter((t) => {
      // fornitore
      if (f.supplierId) {
        if (String(t?.supplierId || "") !== String(f.supplierId)) return false;
      }

      // anno
      if (f.year) {
        const d = new Date(t?.data);
        if (Number.isNaN(d.getTime())) return false;
        if (String(d.getFullYear()) !== String(f.year)) return false;
      }

      // date da/a
      if (fromMs != null || toMs != null) {
        const d = new Date(t?.data);
        const ms = d.getTime();
        if (Number.isNaN(ms)) return false;
        if (fromMs != null && ms < fromMs) return false;
        if (toMs != null && ms > toMs) return false;
      }

      // categoria
      if (f.category) {
        if (String(t?.categoria || "") !== String(f.category)) return false;
      }

      // tipo (robusto: usa tipo, altrimenti importo)
      if (f.type) {
        const tipo = t?.tipo || typeFromAmount(t?.importo) || "";
        if (String(tipo) !== String(f.type)) return false;
      }

      // approvazione
      if (f.approval === "approved" && !t?.approved) return false;
      if (f.approval === "pending" && !!t?.approved) return false;

      // descrizione
      if (descQ) {
        const hay = normalizeText(t?.descrizione);
        if (!hay.includes(descQ)) return false;
      }

      return true;
    });
  }, [transactions, filters]);

  const summary = useMemo(() => {
    let income = 0;
    let expenses = 0;

    for (const t of visibleTransactions) {
      const n = Number(t?.importo);
      if (!Number.isFinite(n)) continue;
      if (n >= 0) income += n;
      else expenses += n; // negativo
    }

    return {
      count: visibleTransactions.length,
      income,
      expenses,
      balance: income + expenses,
    };
  }, [visibleTransactions]);

  /* ---------- Fetch Transactions ---------- */

  const fetchTransactions = useCallback(
    async (query = "") => {
      if (!token) return;

      // annulla richiesta precedente
      if (abortTxRef.current) abortTxRef.current.abort();
      const controller = new AbortController();
      abortTxRef.current = controller;

      setLoading(true);
      setError("");

      try {
        let url = joinUrl(API_BASE, "api/transactions");
        const q = (query || "").trim();
        const params = new URLSearchParams();

        if (q) params.set("search", q);
        if (statusFilter) params.set("status", statusFilter);

        const qs = params.toString();
        if (qs) url += `?${qs}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(await readErrorMessage(res));

        const data = await res.json().catch(() => []);
        setTransactions(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setError(err?.message || "Errore nel recupero delle transazioni");
      } finally {
        setLoading(false);
      }
    },
    [token, statusFilter]
  );

  useEffect(() => {
    if (!token) return;
    fetchTransactions("");
    return () => abortTxRef.current?.abort?.();
  }, [token, fetchTransactions]);

  useEffect(() => {
    if (selectedId && !transactions.some((t) => t.id === selectedId)) setSelectedId(null);
  }, [transactions, selectedId]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchTransactions(searchTerm);
    setSelectedId(null);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    fetchTransactions("");
    setSelectedId(null);
  };

  /* ---------- Delete / Approve ---------- */

  const handleDelete = async (txId) => {
    if (!token || !canDelete) return;

    const ok = window.confirm("Vuoi cancellare questa transazione?");
    if (!ok) return;

    try {
      const res = await fetch(joinUrl(API_BASE, `api/transactions/${txId}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) throw new Error(await readErrorMessage(res));

      setTransactions((prev) => prev.filter((tx) => tx.id !== txId));
      setSelectedId(null);
      window.alert("Transazione cancellata con successo ✅");
    } catch (err) {
      setError(err?.message || "Errore durante la cancellazione");
    }
  };

  const handleApprove = async (txId) => {
    if (!token || !txId) return;

    const ok = window.confirm("Vuoi approvare questa transazione?");
    if (!ok) return;

    setApprovingId(txId);
    setError("");

    try {
      const res = await fetch(joinUrl(API_BASE, `api/transactions/${txId}/approve`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) throw new Error(await readErrorMessage(res));

      const payload = await res.json().catch(() => null);

      setTransactions((prev) =>
        prev.map((t) =>
          t.id !== txId
            ? t
            : {
                ...t,
                ...(payload && typeof payload === "object" ? payload : null),
                approved: true,
              }
        )
      );

      setSelectedId(null);
    } catch (err) {
      setError(err?.message || "Errore durante l'approvazione");
    } finally {
      setApprovingId(null);
    }
  };

  /* ---------- CSV ---------- */

  const exportCsv = () => {
    if (!visibleTransactions.length) return;

    const headers = [
      "id",
      "data",
      "descrizione",
      "importo",
      "tipo",
      "categoria",
      "paymentMethod",
      "supplierId",
      "supplierName",
      "approved",
      "receiptPath",
    ];

    const lines = [
      headers.map(csvEscape).join(","),
      ...visibleTransactions.map((t) =>
        [
          t.id,
          t.data,
          t.descrizione,
          t.importo,
          t.tipo,
          t.categoria,
          t.paymentMethod,
          t.supplierId,
          t.supplierName,
          t.approved,
          t.receiptPath,
        ]
          .map(csvEscape)
          .join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `transazioni_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* =========================================================
     EDIT MODAL
     ========================================================= */

  const fetchSuppliers = useCallback(async () => {
    if (!token) return;
    setSuppliersLoading(true);
    try {
      const res = await fetch(joinUrl(API_BASE, "api/suppliers"), {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const data = await res.json().catch(() => []);
      const arr = Array.isArray(data) ? data : [];
      arr.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
      setSuppliers(arr);
    } catch (e) {
      console.warn("Errore caricamento fornitori:", e);
    } finally {
      setSuppliersLoading(false);
    }
  }, [token]);

  /**
   * ✅ FIX PRINCIPALE:
   * - Se la categoria esiste in categories[] ed ha un type, quel type vince sul tipo/importo.
   */
  const openEditModal = (tx) => {
    if (!tx) return;

    const amount = Number(tx.importo);
    const tipoFromAmount = typeFromAmount(amount) || "Entrata";

    const catName = tx.categoria || "";
    const catObj = getCategoryObj(catName);

    const tipo = (catObj?.type || tx.tipo || tipoFromAmount || "Entrata").trim();
    const absAmount = Number.isFinite(amount) ? Math.abs(amount) : "";

    setEditTx({
      id: tx.id,
      data: tx.data ? String(tx.data).slice(0, 10) : todayIso(),
      descrizione: tx.descrizione || "",
      tipo,
      categoria: catName || "",
      paymentMethod: tx.paymentMethod || "Bonifico",
      importo: absAmount === "" ? "" : String(absAmount),
      coloreCategoria: tx.coloreCategoria,
      supplierId: tx.supplierId ? String(tx.supplierId) : "",
      supplierName: tx.supplierName || "",
      receiptPath: tx.receiptPath || "",
      approved: !!tx.approved,
    });

    setSupplierQueryEdit("");
    setEditOpen(true);
    fetchSuppliers();
  };

  const closeEditModal = useCallback(() => {
    if (editSaving) return;
    setEditOpen(false);
    setEditTx(null);
  }, [editSaving]);

  // ESC per chiudere (EDIT e FILTRI)
  useEffect(() => {
    if (!editOpen && !filterOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (editOpen) closeEditModal();
        if (filterOpen) setFilterOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editOpen, filterOpen, closeEditModal]);

  // ✅ categorie coerenti con tipo (nel modal) + include categoria corrente se "fuori lista"
  const filteredCategories = useMemo(() => {
    const tipo = editTx?.tipo || "Entrata";
    const base =
      tipo === "Entrata"
        ? categories.filter((c) => c.type === "Entrata")
        : categories.filter((c) => c.type === "Uscita");

    const cur = editTx?.categoria;
    if (cur && !base.some((c) => c.name === cur)) {
      return [{ name: cur, type: "mixed" }, ...base];
    }
    return base;
  }, [editTx?.tipo, editTx?.categoria]);

  // se categoria vuota, aggancia la prima coerente
  useEffect(() => {
    if (!editOpen || !editTx) return;
    if (editTx.categoria) return;
    const first = filteredCategories[0]?.name;
    if (!first) return;
    setEditTx((p) => ({ ...p, categoria: first }));
  }, [editOpen, editTx, filteredCategories]);

  // ✅ quando cambia categoria, riallinea automaticamente il tipo (SENZA disable eslint)
  const editCatType = useMemo(() => {
    if (!editOpen || !editTx?.categoria) return "";
    return getCategoryObj(editTx.categoria)?.type || "";
  }, [editOpen, editTx?.categoria]);

  useEffect(() => {
    if (!editOpen || !editTx) return;
    if (!editCatType) return;
    if (editTx.tipo === editCatType) return;
    setEditTx((p) => ({ ...p, tipo: editCatType }));
  }, [editOpen, editTx, editCatType]);

  const filteredSuppliersFilter = useMemo(() => {
    const q = normalizeText(supplierQueryFilter);
    if (!q) return suppliers;
    return suppliers.filter((s) => {
      const name = normalizeText(s?.name);
      const email = normalizeText(s?.email);
      const phone = normalizeText(s?.phone);
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [suppliers, supplierQueryFilter]);

  const filteredSuppliersEdit = useMemo(() => {
    const q = normalizeText(supplierQueryEdit);
    if (!q) return suppliers;
    return suppliers.filter((s) => {
      const name = normalizeText(s?.name);
      const email = normalizeText(s?.email);
      const phone = normalizeText(s?.phone);
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [suppliers, supplierQueryEdit]);

  const onPickSupplier = (idStr) => {
    const id = String(idStr || "");
    const found = suppliers.find((s) => String(s.id) === id) || null;

    setEditTx((p) => ({
      ...p,
      supplierId: id,
      supplierName: found?.name || p?.supplierName || "",
    }));

    setTimeout(() => payMethodRef.current?.focus?.(), 0);
  };

  const saveEdit = async () => {
    if (!token || !editTx?.id) return;

    const descrizione = String(editTx.descrizione || "").trim();
    const categoria = String(editTx.categoria || "").trim();
    const tipo = String(editTx.tipo || "").trim();
    const paymentMethod = String(editTx.paymentMethod || "").trim();
    const d = String(editTx.data || "").trim();
    const amount = Number(editTx.importo);

    if (!descrizione) return setError("Descrizione obbligatoria.");
    if (!categoria) return setError("Categoria obbligatoria.");
    if (!tipo) return setError("Tipo obbligatorio.");
    if (!paymentMethod) return setError("Metodo di pagamento obbligatorio.");
    if (!d) return setError("Data obbligatoria.");
    if (!Number.isFinite(amount)) return setError("Importo non valido.");

    const signedAmount = tipo === "Entrata" ? Math.abs(amount) : -Math.abs(amount);
    const coloreCategoria = getCategoryColor(categoria);

    const payload = {
      data: d,
      descrizione,
      tipo,
      categoria,
      importo: signedAmount,
      paymentMethod,
      coloreCategoria,
      supplierId: editTx.supplierId ? editTx.supplierId : null,
      supplierName: editTx.supplierName ? editTx.supplierName : null,
    };

    setEditSaving(true);
    setError("");

    try {
      const res = await fetch(joinUrl(API_BASE, `api/transactions/${editTx.id}`), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await readErrorMessage(res));

      const updated = await res.json().catch(() => null);
      const merged = updated && typeof updated === "object" ? updated : payload;

      setTransactions((prev) => prev.map((t) => (t.id === editTx.id ? { ...t, ...merged } : t)));

      setEditOpen(false);
      setEditTx(null);
      setSelectedId(null);
    } catch (e) {
      setError(e?.message || "Errore durante il salvataggio.");
    } finally {
      setEditSaving(false);
    }
  };

  /* ---------- UI ---------- */

  const txDotColor = useCallback((t) => t?.coloreCategoria || getCategoryColor(t?.categoria), []);

  return (
    <div className="tx-page">
      <header className="tx-header">
        <div className="tx-header__title">
          <h2 className="tx-title">Transazioni</h2>
          <p className="tx-subtitle">
            Consulta, cerca e visualizza i dettagli delle transazioni.{" "}
            {canApprove ? "Puoi anche approvare quelle in attesa." : ""}
          </p>
        </div>

        <div className="tx-header__actions">
          <button
            type="button"
            className="tx-btn tx-btn--ghost"
            onClick={() => fetchTransactions(searchTerm)}
            disabled={loading}
            title="Ricarica elenco"
          >
            Aggiorna
          </button>

          <button
            type="button"
            className="tx-btn tx-btn--ghost"
            onClick={() => {
              setFilterOpen(true);
              fetchSuppliers();
            }}
            disabled={loading}
            title="Apri filtri avanzati"
          >
            Filtri
          </button>

          <button
            type="button"
            className="tx-btn tx-btn--ghost"
            onClick={exportCsv}
            disabled={loading || visibleTransactions.length === 0}
            title="Esporta l’elenco corrente (filtrato) in CSV"
          >
            Esporta CSV
          </button>
        </div>
      </header>

      {!token && (
        <div className="tx-alert tx-alert--warning" role="alert">
          Sessione non valida: effettua il login per vedere le transazioni.
        </div>
      )}

      <form className="tx-search" onSubmit={handleSearchSubmit}>
        <div className="tx-search__field">
          <span className="tx-search__icon" aria-hidden="true">
            🔎
          </span>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca (server) per descrizione, categoria, metodo o fornitore..."
            aria-label="Cerca transazioni"
          />
          {!!searchTerm && (
            <button
              type="button"
              className="tx-icon-btn"
              onClick={handleClearSearch}
              aria-label="Svuota ricerca"
              title="Svuota"
            >
              ✕
            </button>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="tx-search__filter"
          aria-label="Filtro stato (server)"
        >
          <option value="">Tutti</option>
          <option value="approved">Approvata</option>
          <option value="pending">In attesa</option>
        </select>

        <button className="tx-btn tx-btn--primary" type="submit" disabled={loading}>
          Cerca
        </button>
      </form>

      {error && (
        <div className="tx-alert tx-alert--error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="tx-loading" role="status" aria-live="polite">
          <div className="tx-spinner" aria-hidden="true" />
          <span>Caricamento transazioni...</span>
        </div>
      ) : selectedTx ? (
        /* =========================
           DETTAGLIO
           ========================= */
        <section className="tx-card">
          <div className="tx-card__header">
            <div>
              <h3 className="tx-card__title">Dettaglio transazione</h3>
              <p className="tx-card__subtitle">ID: {selectedTx.id}</p>
            </div>

            <div className="tx-card__headerActions">
              <button type="button" className="tx-btn tx-btn--ghost" onClick={() => setSelectedId(null)}>
                Indietro
              </button>

              {canDelete && (
                <button
                  type="button"
                  className="tx-btn tx-btn--danger"
                  onClick={() => handleDelete(selectedTx.id)}
                >
                  Cancella
                </button>
              )}

              {canEdit && (!selectedTx.approved || canEditApproved) && (
                <button
                  type="button"
                  className="tx-btn tx-btn--primary"
                  onClick={() => openEditModal(selectedTx)}
                >
                  Modifica
                </button>
              )}

              {canApprove && !selectedTx.approved && (
                <button
                  type="button"
                  className="tx-btn tx-btn--primary"
                  onClick={() => handleApprove(selectedTx.id)}
                  disabled={approvingId === selectedTx.id}
                >
                  {approvingId === selectedTx.id ? "Approvazione..." : "Approva"}
                </button>
              )}
            </div>
          </div>

          <div className="tx-detailGrid">
            <div className="tx-detailItem">
              <div className="tx-label">Data</div>
              <div className="tx-value">{formatDate(selectedTx.data)}</div>
            </div>

            <div className="tx-detailItem">
              <div className="tx-label">Importo</div>
              <div
                className={`tx-value tx-value--amount ${
                  Number(selectedTx.importo) < 0 ? "is-negative" : "is-positive"
                }`}
              >
                {formatMoney(selectedTx.importo)}
              </div>
            </div>

            <div className="tx-detailItem tx-detailItem--wide">
              <div className="tx-label">Descrizione</div>
              <div className="tx-value">{selectedTx.descrizione || "—"}</div>
            </div>

            <div className="tx-detailItem">
              <div className="tx-label">Tipo</div>
              <div className="tx-value">
                <Badge variant="muted">{selectedTx.tipo || typeFromAmount(selectedTx.importo) || "—"}</Badge>
              </div>
            </div>

            <div className="tx-detailItem">
              <div className="tx-label">Categoria</div>
              <div className="tx-value tx-inline">
                <Dot color={txDotColor(selectedTx)} />
                <span>{selectedTx.categoria || "—"}</span>
              </div>
            </div>

            <div className="tx-detailItem">
              <div className="tx-label">Fornitore</div>
              <div className="tx-value">
                {selectedTx.supplierName ? (
                  <Badge variant="muted">{selectedTx.supplierName}</Badge>
                ) : selectedTx.supplierId ? (
                  <span className="tx-muted">ID: {String(selectedTx.supplierId)}</span>
                ) : (
                  "—"
                )}
              </div>
            </div>

            <div className="tx-detailItem">
              <div className="tx-label">Metodo di pagamento</div>
              <div className="tx-value tx-inline">
                {paymentIcons[selectedTx.paymentMethod] ? (
                  <span aria-hidden="true">{paymentIcons[selectedTx.paymentMethod]}</span>
                ) : null}
                <span>{selectedTx.paymentMethod || "—"}</span>
              </div>
            </div>

            <div className="tx-detailItem">
              <div className="tx-label">Stato</div>
              <div className="tx-value">
                {selectedTx.approved ? (
                  <Badge variant="success">✔️ Approvata</Badge>
                ) : (
                  <Badge variant="warning">⏳ In attesa</Badge>
                )}
              </div>
            </div>
          </div>

          {selectedTx.receiptPath && (
            <div className="tx-receipt">
              <h4 className="tx-sectionTitle">Ricevuta</h4>

              {(() => {
                const url = buildUploadUrl(selectedTx.receiptPath);
                const isImage = String(selectedTx.receiptPath).match(/\.(jpg|jpeg|png|gif|webp)$/i);

                return (
                  <>
                    {isImage ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="tx-receiptPreview">
                        <img src={url} alt="Ricevuta" />
                      </a>
                    ) : (
                      <p className="tx-muted">File allegato non immagine. Puoi aprirlo o scaricarlo.</p>
                    )}

                    <div className="tx-receiptActions">
                      <a className="tx-btn tx-btn--ghost" href={url} target="_blank" rel="noopener noreferrer">
                        Apri
                      </a>
                      <a className="tx-btn tx-btn--ghost" href={url} download>
                        Scarica
                      </a>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </section>
      ) : (
        <>
          <section className="tx-summary">
            <div className="tx-summary__item">
              <div className="tx-summary__label">Risultati</div>
              <div className="tx-summary__value">{summary.count}</div>
            </div>

            <div className="tx-summary__item">
              <div className="tx-summary__label">Entrate</div>
              <div className="tx-summary__value is-positive">{formatMoney(summary.income)}</div>
            </div>

            <div className="tx-summary__item">
              <div className="tx-summary__label">Uscite</div>
              <div className="tx-summary__value is-negative">{formatMoney(summary.expenses)}</div>
            </div>

            <div className="tx-summary__item">
              <div className="tx-summary__label">Saldo</div>
              <div className={`tx-summary__value ${summary.balance < 0 ? "is-negative" : "is-positive"}`}>
                {formatMoney(summary.balance)}
              </div>
            </div>
          </section>

          {visibleTransactions.length === 0 ? (
            <div className="tx-empty">
              <div className="tx-empty__icon" aria-hidden="true">
                🧾
              </div>
              <div className="tx-empty__title">Nessuna transazione trovata</div>
              <div className="tx-empty__text">
                Prova a cambiare i filtri (avanzati o ricerca) oppure ricarica la pagina.
              </div>
              <div className="tx-empty__actions">
                <button className="tx-btn tx-btn--primary" type="button" onClick={() => fetchTransactions("")}>
                  Ricarica
                </button>

                {!!searchTerm && (
                  <button className="tx-btn tx-btn--ghost" type="button" onClick={handleClearSearch}>
                    Svuota ricerca
                  </button>
                )}

                <button
                  className="tx-btn tx-btn--ghost"
                  type="button"
                  onClick={() => {
                    setFilters({
                      supplierId: "",
                      dateFrom: "",
                      dateTo: "",
                      year: "",
                      category: "",
                      type: "",
                      approval: "",
                      description: "",
                    });
                    setSupplierQueryFilter("");
                  }}
                >
                  Pulisci filtri
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ✅ MOBILE LIST (cards) */}
              <div className="tx-mobileList" role="list" aria-label="Transazioni (mobile)">
                {visibleTransactions.map((t) => {
                  const amount = Number(t.importo);
                  const negative = Number.isFinite(amount) && amount < 0;

                  return (
                    <button
                      key={t.id}
                      type="button"
                      className="tx-mobileCard"
                      onClick={() => setSelectedId(t.id)}
                      aria-label={`Apri transazione ${t.descrizione || t.id}`}
                    >
                      <div className="tx-mobileTop">
                        <div className="tx-mobileDate">{formatDate(t.data)}</div>
                        <div className={`tx-mobileAmount ${negative ? "is-negative" : "is-positive"}`}>
                          {formatMoney(t.importo)}
                        </div>
                      </div>

                      <div className="tx-mobileDesc">{t.descrizione || "—"}</div>

                      <div className="tx-mobileMeta">
                        <span className="tx-chip tx-chip--primary">
                          <Dot color={txDotColor(t)} />
                          <span className="tx-chipText">{t.categoria || "—"}</span>
                        </span>

                        <span className="tx-chip">
                          {paymentIcons[t.paymentMethod] ? (
                            <span aria-hidden="true">{paymentIcons[t.paymentMethod]}</span>
                          ) : null}
                          <span className="tx-chipText">{t.paymentMethod || "—"}</span>
                        </span>

                        <span className="tx-chip">
                          <span className="tx-chipText">
                            {t.supplierName
                              ? `🧾 ${t.supplierName}`
                              : t.supplierId
                              ? `🧾 ID: ${String(t.supplierId)}`
                              : "🧾 —"}
                          </span>
                        </span>

                        {t.approved ? (
                          <Badge variant="success">✔️ Approvata</Badge>
                        ) : (
                          <Badge variant="warning">⏳ In attesa</Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* ✅ DESKTOP TABLE */}
              <div className="tx-tableWrapper" role="region" aria-label="Elenco transazioni">
                <table className="tx-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrizione</th>
                      <th className="tx-right">Importo</th>
                      <th>Tipo</th>
                      <th>Categoria</th>
                      <th>Fornitore</th>
                      <th>Metodo</th>
                      <th>Stato</th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleTransactions.map((t) => {
                      const amount = Number(t.importo);
                      const negative = Number.isFinite(amount) && amount < 0;

                      return (
                        <tr
                          key={t.id}
                          className={`tx-trow ${negative ? "tx-trow--negative" : "tx-trow--positive"}`}
                          onClick={() => setSelectedId(t.id)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedId(t.id);
                            }
                          }}
                          aria-label={`Apri dettaglio transazione ${t.descrizione || t.id}`}
                        >
                          <td className="tx-nowrap">{formatDate(t.data)}</td>

                          <td className="tx-ellipsis" title={t.descrizione || ""}>
                            {t.descrizione || "—"}
                          </td>

                          <td className={`tx-right tx-amount ${negative ? "is-negative" : "is-positive"}`}>
                            {formatMoney(t.importo)}
                          </td>

                          <td>
                            <Badge variant="muted">{t.tipo || typeFromAmount(t.importo) || "—"}</Badge>
                          </td>

                          <td className="tx-nowrap">
                            <Dot color={txDotColor(t)} />
                            {t.categoria || "—"}
                          </td>

                          <td className="tx-nowrap">
                            {t.supplierName ? (
                              <span className="tx-supplierCell" title={t.supplierName}>
                                {t.supplierName}
                              </span>
                            ) : t.supplierId ? (
                              <span className="tx-muted">ID: {String(t.supplierId)}</span>
                            ) : (
                              "—"
                            )}
                          </td>

                          <td className="tx-nowrap">
                            {paymentIcons[t.paymentMethod] ? (
                              <span aria-hidden="true" className="tx-payIcon">
                                {paymentIcons[t.paymentMethod]}
                              </span>
                            ) : null}
                            {t.paymentMethod || "—"}
                          </td>

                          <td>
                            {t.approved ? (
                              <Badge variant="success">✔️ Approvata</Badge>
                            ) : (
                              <Badge variant="warning">⏳ In attesa</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="tx-tableHint">Suggerimento: clicca su una riga (o premi Invio) per vedere il dettaglio.</div>
              </div>
            </>
          )}
        </>
      )}

      {/* =========================================================
          MODAL FILTRI AVANZATI (click fuori + ESC)
         ========================================================= */}
      {filterOpen && (
        <div
          className="tx-modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Filtri avanzati"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFilterOpen(false);
          }}
        >
          <div className="tx-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="tx-modalHeader">
              <div>
                <div className="tx-modalTitle">Filtri avanzati</div>
                <div className="tx-modalSub">
                  Filtra per fornitore, date, anno, categoria, tipo, approvazione e descrizione.
                </div>
              </div>

              <button className="tx-iconBtn" type="button" onClick={() => setFilterOpen(false)} aria-label="Chiudi">
                ✕
              </button>
            </div>

            <div className="tx-modalBody">
              <div className="tx-formGrid">
                {/* Fornitore */}
                <div className="tx-field tx-field--wide">
                  <label className="tx-fieldLabel">Fornitore</label>

                  <input
                    type="text"
                    className="tx-input"
                    value={supplierQueryFilter}
                    onChange={(e) => setSupplierQueryFilter(e.target.value)}
                    placeholder={suppliersLoading ? "Caricamento fornitori..." : "Cerca fornitore per nome/email/telefono..."}
                  />

                  <div className="tx-supplierPicker">
                    <button
                      type="button"
                      className={`tx-supplierItem ${!filters.supplierId ? "is-active" : ""}`}
                      onClick={() => setFilters((p) => ({ ...p, supplierId: "" }))}
                    >
                      Tutti i fornitori
                    </button>

                    {(filteredSuppliersFilter || []).slice(0, 20).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className={`tx-supplierItem ${String(filters.supplierId) === String(s.id) ? "is-active" : ""}`}
                        onClick={() => setFilters((p) => ({ ...p, supplierId: String(s.id) }))}
                        title={s?.email || ""}
                      >
                        <span className="tx-supplierName">{s?.name || `ID ${s.id}`}</span>
                        {s?.email ? <span className="tx-muted"> · {s.email}</span> : null}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Anno */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Anno</label>
                  <select
                    className="tx-select"
                    value={filters.year}
                    onChange={(e) => setFilters((p) => ({ ...p, year: e.target.value }))}
                  >
                    <option value="">Tutti</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Approvazione */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Approvazione</label>
                  <select
                    className="tx-select"
                    value={filters.approval}
                    onChange={(e) => setFilters((p) => ({ ...p, approval: e.target.value }))}
                  >
                    <option value="">Tutte</option>
                    <option value="approved">Approvate</option>
                    <option value="pending">In attesa</option>
                  </select>
                </div>

                {/* Tipo */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Tipo</label>
                  <select
                    className="tx-select"
                    value={filters.type}
                    onChange={(e) => setFilters((p) => ({ ...p, type: e.target.value }))}
                  >
                    <option value="">Tutti</option>
                    <option value="Entrata">Entrata</option>
                    <option value="Uscita">Uscita</option>
                  </select>
                </div>

                {/* Categoria */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Categoria</label>
                  <select
                    className="tx-select"
                    value={filters.category}
                    onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
                  >
                    <option value="">Tutte</option>
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Da</label>
                  <input
                    type="date"
                    className="tx-input"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))}
                  />
                </div>

                <div className="tx-field">
                  <label className="tx-fieldLabel">A</label>
                  <input
                    type="date"
                    className="tx-input"
                    value={filters.dateTo}
                    onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))}
                  />
                </div>

                {/* Descrizione */}
                <div className="tx-field tx-field--wide">
                  <label className="tx-fieldLabel">Descrizione contiene</label>
                  <input
                    type="text"
                    className="tx-input"
                    value={filters.description}
                    onChange={(e) => setFilters((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Es. carburante, manutenzione, donazione…"
                  />
                </div>

                <div className="tx-hint tx-field--wide">
                  💡 I filtri avanzati sono <b>client-side</b>: agiscono sui dati già scaricati dal server.
                </div>
              </div>
            </div>

            <div className="tx-modalFooter">
              <button
                className="tx-btn tx-btn--ghost"
                type="button"
                onClick={() => {
                  setFilters({
                    supplierId: "",
                    dateFrom: "",
                    dateTo: "",
                    year: "",
                    category: "",
                    type: "",
                    approval: "",
                    description: "",
                  });
                  setSupplierQueryFilter("");
                }}
              >
                Pulisci
              </button>

              <button className="tx-btn tx-btn--ghost" type="button" onClick={() => setFilterOpen(false)}>
                Chiudi
              </button>

              <button className="tx-btn tx-btn--primary" type="button" onClick={() => setFilterOpen(false)}>
                Applica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL EDIT (click fuori + ESC)
         ========================================================= */}
      {editOpen && editTx && (
        <div
          className="tx-modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Modifica transazione"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEditModal();
          }}
        >
          <div className="tx-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="tx-modalHeader">
              <div>
                <div className="tx-modalTitle">Modifica transazione</div>
                <div className="tx-modalSub">ID: {editTx.id}</div>
              </div>

              <button className="tx-iconBtn" type="button" onClick={closeEditModal} aria-label="Chiudi">
                ✕
              </button>
            </div>

            <div className="tx-modalBody">
              <div className="tx-formGrid">
                {/* Data */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Data</label>
                  <input
                    type="date"
                    className="tx-input"
                    value={editTx.data}
                    onChange={(e) => setEditTx((p) => ({ ...p, data: e.target.value }))}
                  />
                </div>

                {/* Importo */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Importo</label>
                  <input
                    type="number"
                    className="tx-input"
                    value={editTx.importo}
                    min="0"
                    step="0.01"
                    onChange={(e) => setEditTx((p) => ({ ...p, importo: e.target.value }))}
                    placeholder="0,00"
                  />
                  <div className="tx-help">
                    💡 Verrà salvato come <b>{editTx.tipo === "Uscita" ? "negativo" : "positivo"}</b>.
                  </div>
                </div>

                {/* Descrizione */}
                <div className="tx-field tx-field--wide">
                  <label className="tx-fieldLabel">Descrizione</label>
                  <input
                    type="text"
                    className="tx-input"
                    value={editTx.descrizione}
                    onChange={(e) => setEditTx((p) => ({ ...p, descrizione: e.target.value }))}
                    placeholder="Es. Rimborso, Donazione, Carburante..."
                  />
                </div>

                {/* Tipo */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Tipo</label>
                  <select
                    className="tx-select"
                    value={editTx.tipo}
                    onChange={(e) =>
                      setEditTx((p) => ({
                        ...p,
                        tipo: e.target.value,
                        categoria: "", // forza ricalcolo coerente
                      }))
                    }
                  >
                    <option value="Entrata">Entrata</option>
                    <option value="Uscita">Uscita</option>
                  </select>
                </div>

                {/* Categoria */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Categoria</label>
                  <select
                    className="tx-select"
                    value={editTx.categoria}
                    onChange={(e) => setEditTx((p) => ({ ...p, categoria: e.target.value }))}
                  >
                    {(filteredCategories || []).map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>

                  <div className="tx-inlineHint">
                    <Dot color={getCategoryColor(editTx.categoria)} />
                    <span className="tx-muted">Colore auto</span>
                  </div>
                </div>

                {/* Metodo pagamento */}
                <div className="tx-field">
                  <label className="tx-fieldLabel">Metodo di pagamento</label>
                  <select
                    ref={payMethodRef}
                    className="tx-select"
                    value={editTx.paymentMethod}
                    onChange={(e) => setEditTx((p) => ({ ...p, paymentMethod: e.target.value }))}
                  >
                    <option value="Bonifico">🏦 Bonifico</option>
                    <option value="Contanti">💶 Contanti</option>
                    <option value="Pagamento Elettronico">💳 Pagamento Elettronico</option>
                  </select>
                </div>

                {/* Fornitore */}
                <div className="tx-field tx-field--wide">
                  <label className="tx-fieldLabel">Fornitore</label>

                  <div className="tx-row">
                    <input
                      type="text"
                      className="tx-input"
                      value={supplierQueryEdit}
                      onChange={(e) => setSupplierQueryEdit(e.target.value)}
                      placeholder={suppliersLoading ? "Caricamento fornitori..." : "Cerca fornitore..."}
                    />

                    <button
                      type="button"
                      className="tx-btn tx-btn--ghost"
                      onClick={() =>
                        setEditTx((p) => ({
                          ...p,
                          supplierId: "",
                          supplierName: "",
                        }))
                      }
                      title="Rimuovi fornitore"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="tx-supplierPicker">
                    {(filteredSuppliersEdit || []).slice(0, 12).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className={`tx-supplierItem ${String(editTx.supplierId) === String(s.id) ? "is-active" : ""}`}
                        onClick={() => onPickSupplier(String(s.id))}
                        title={s?.email || ""}
                      >
                        <span className="tx-supplierName">{s?.name || `ID ${s.id}`}</span>
                        {s?.email ? <span className="tx-muted"> · {s.email}</span> : null}
                      </button>
                    ))}
                  </div>

                  <div className="tx-help">
                    Selezionato:{" "}
                    {editTx.supplierName ? <b>{editTx.supplierName}</b> : editTx.supplierId ? `ID ${editTx.supplierId}` : "—"}
                  </div>
                </div>

                {/* Ricevuta */}
                <div className="tx-field tx-field--wide">
                  <label className="tx-fieldLabel">Ricevuta</label>
                  {editTx.receiptPath ? (
                    <div className="tx-row tx-row--wrap">
                      <span className="tx-muted">{editTx.receiptPath}</span>
                      <a
                        className="tx-btn tx-btn--ghost"
                        href={buildUploadUrl(editTx.receiptPath)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Apri
                      </a>
                    </div>
                  ) : (
                    <div className="tx-muted">—</div>
                  )}
                </div>

                {/* Stato */}
                <div className="tx-field tx-field--wide">
                  <label className="tx-fieldLabel">Stato</label>
                  {editTx.approved ? <Badge variant="success">✔️ Approvata</Badge> : <Badge variant="warning">⏳ In attesa</Badge>}
                  {!canEditApproved && editTx.approved ? (
                    <div className="tx-help">🔒 Approvata: non modificabile per il tuo ruolo.</div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="tx-modalFooter">
              <button className="tx-btn tx-btn--ghost" type="button" onClick={closeEditModal} disabled={editSaving}>
                Annulla
              </button>
              <button className="tx-btn tx-btn--primary" type="button" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? "Salvataggio..." : "Salva modifiche"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
